import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { updateSEOMeta, resetSEOMeta, useBlogLanguage } from '../utils/seoUtils';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hi%2C%20I%20want%20to%20schedule%20a%20free%20consultation%20with%20Marea%20Riviera";

export default function BlogCostOfLiving() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  useBlogLanguage('en');

  useEffect(() => {
    updateSEOMeta({
      title: es ? 'Costo de Vida en Puerto Morelos 2026: Desglose Mensual Completo | Marea Riviera' : 'Cost of Living in Puerto Morelos 2026: Complete Monthly Breakdown | Marea Riviera',
      description: es ? '¿Cuánto cuesta realmente vivir en Puerto Morelos en 2026? Renta, comida, servicios, salud y más — desglose honesto para expats, jubilados y trabajadores remotos.' : 'How much does it really cost to live in Puerto Morelos, Mexico in 2026? Rent, food, utilities, healthcare and more — a honest breakdown for expats, retirees and remote workers.',
      url: 'https://mareariviera.com/blog/cost-of-living-in-puerto-morelos-2026',
      lang: es ? 'es' : 'en'
    });
    window.scrollTo(0, 0);
    return () => { resetSEOMeta(); };
  }, [es]);

  const scrollToContact = () => { sessionStorage.setItem('scrollTo', 'contact'); navigate('/'); };

  return (
    <div className="bg-[#FAFAF7] min-h-screen pt-[120px]">
      <div className="bg-[#0A2235] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#2BBFBF] hover:text-[#7ECFCF] transition-colors mb-8 font-inter text-sm" data-testid="blog-back-btn">
            <ArrowLeft className="w-4 h-4" /> {es ? 'Volver al inicio' : 'Back to home'}
          </button>
          <div className="flex items-center gap-4 text-[#FAFAF7]/50 font-inter text-sm mb-6">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {es ? '3 de julio, 2026' : 'July 3, 2026'}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> 12 min read</span>
          </div>
          <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-tight mb-6" data-testid="blog-title">
            {es ? 'Costo de Vida en Puerto Morelos, México: Desglose Honesto 2026' : 'Cost of Living in Puerto Morelos, Mexico: The Honest 2026 Breakdown'}
          </h1>
          <p className="font-inter text-[#FAFAF7]/70 text-lg leading-relaxed">
            {es ? '¿Cuánto cuesta realmente vivir en Puerto Morelos? Renta, comida, servicios, salud y más — desglose honesto para expats, jubilados y trabajadores remotos.' : 'How much does it really cost to live in Puerto Morelos? Rent, food, utilities, healthcare and more — an honest breakdown for expats, retirees and remote workers.'}
          </p>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-content">
        <div className="prose-marea">
          {es ? (<>
            <p>Puerto Morelos ocupa una posición interesante para cualquiera que considere mudarse a la Riviera Maya. Es más caro que los pueblos mexicanos del interior — es un destino turístico en la costa caribeña — pero notablemente más barato que Playa del Carmen y Tulum por calidad de vida comparable.</p>

            <h2>La respuesta corta</h2>
            <p>Una persona puede vivir cómodamente en Puerto Morelos por <strong>$1,500–$2,200 USD al mes</strong>. Una pareja típicamente gasta <strong>$2,000–$3,000 USD</strong> según estilo de vida. Eso incluye renta, comida, servicios, transporte y entretenimiento — pero no seguro médico ni viajes grandes.</p>
            <p>Si vienes de EE.UU. o Canadá, probablemente verás un <strong>ahorro de 40–60%</strong> comparado con un estilo de vida similar.</p>

            <h2>Renta: tu gasto más grande</h2>
            <p>La renta varía significativamente dependiendo de qué tan cerca quieras estar de la playa y cuánto espacio necesites.</p>
            <h3>Lado colonia (oeste de la carretera):</h3>
            <ul>
              <li>Estudio o 1 recámara: $400–$700 USD/mes</li>
              <li>2 recámaras: $600–$1,000 USD/mes</li>
              <li>Casa: $800–$1,500 USD/mes</li>
            </ul>
            <h3>Lado playa / centro:</h3>
            <ul>
              <li>Estudio o 1 recámara: $800–$1,200 USD/mes</li>
              <li>2 recámaras: $1,200–$2,000 USD/mes</li>
              <li>Casa frente al mar: $2,000–$4,000+ USD/mes</li>
            </ul>

            <h2>Comida y supermercado</h2>
            <ul>
              <li><strong>Supermercado mensual (1 persona):</strong> $250–$400 USD</li>
              <li><strong>Comida en restaurante local:</strong> $5–$12 USD</li>
              <li><strong>Comida en restaurante de turistas:</strong> $15–$30 USD</li>
              <li><strong>Cerveza en bar local:</strong> $2–$4 USD</li>
              <li><strong>Café latte:</strong> $2–$4 USD</li>
            </ul>

            <h2>Servicios y utilidades</h2>
            <ul>
              <li><strong>Electricidad (CFE):</strong> $30–$150 USD/bimestre (el AC lo mueve mucho)</li>
              <li><strong>Agua:</strong> $10–$20 USD/mes</li>
              <li><strong>Gas:</strong> $15–$30 USD/mes</li>
              <li><strong>Internet (fibra 50-100 Mbps):</strong> $25–$40 USD/mes</li>
              <li><strong>Plan celular:</strong> $10–$20 USD/mes</li>
            </ul>

            <h2>Transporte</h2>
            <ul>
              <li><strong>Colectivo a Playa del Carmen:</strong> $1.50 USD</li>
              <li><strong>Colectivo a Cancún:</strong> $2 USD</li>
              <li><strong>Taxi dentro del pueblo:</strong> $2–$5 USD</li>
              <li><strong>Gasolina:</strong> $60–$100 USD/mes si tienes auto</li>
            </ul>

            <h2>Salud</h2>
            <ul>
              <li><strong>Consulta médica (clínica privada):</strong> $30–$60 USD</li>
              <li><strong>Limpieza dental:</strong> $30–$50 USD</li>
              <li><strong>Seguro médico privado:</strong> $100–$300 USD/mes según edad y cobertura</li>
              <li><strong>Mayoría de medicamentos:</strong> Disponibles sin receta a fracción de precios de EE.UU.</li>
            </ul>
            <p>Muchos expats — especialmente jubilados — encuentran que sus costos de salud bajan dramáticamente después de mudarse. Si estás planeando la mudanza, nuestra guía completa de <Link to="/blog/moving-to-puerto-morelos-2026" className="text-[#2BBFBF] underline hover:text-[#0A2235] transition-colors">reubicación a Puerto Morelos</Link> cubre visas, rentas y el cronograma completo.</p>

            <h2>Entretenimiento y estilo de vida</h2>
            <ul>
              <li>Snorkel o buceo: $30–$60 USD</li>
              <li>Clase de yoga: $8–$15 USD</li>
              <li>Cerveza en bar local: $2–$4 USD</li>
              <li>Cine (Cancún): $5–$8 USD</li>
              <li>Excursión a cenotes: $20–$40 USD</li>
            </ul>
            <p>La mayoría que vive aquí encuentra que su gasto en entretenimiento baja naturalmente — la playa es gratis, el arrecife es accesible, y el ritmo de vida no demanda gasto constante.</p>

            <h2>La conclusión</h2>
            <p>Puerto Morelos ofrece una de las mejores propuestas de valor en la Riviera Maya para personas que quieren vida caribeña sin precios de Tulum o multitudes de Playa del Carmen.</p>
            <p>No será el lugar más barato donde hayas vivido. Pero puede ser el mejor valor por lo que recibes: agua cálida, un arrecife saludable, una comunidad genuina y acceso a 20 minutos de un aeropuerto internacional — por una fracción de lo que cuesta calidad similar en EE.UU., Canadá o Europa.</p>
          </>) : (<>
          <p>Puerto Morelos sits in an interesting position for anyone considering a move to the Riviera Maya. It's more expensive than inland Mexican towns — this is a Caribbean coast tourist destination, after all — but noticeably cheaper than Playa del Carmen and Tulum for comparable quality of life.</p>
          <p>This guide breaks down what you'll actually spend living in Puerto Morelos in 2026, across every major category.</p>

          <h2>The Short Answer</h2>
          <p>A single person can live comfortably in Puerto Morelos for <strong>$1,500–$2,200 USD per month</strong>. A couple typically spends <strong>$2,000–$3,000 USD</strong> depending on lifestyle. That includes rent, food, utilities, transportation, and entertainment — but not health insurance or major travel.</p>
          <p>If you're coming from the US or Canada, you're likely looking at <strong>40–60% savings</strong> compared to a similar lifestyle back home.</p>

          <h2>Rent: Your Biggest Expense</h2>
          <p>Rent varies significantly depending on how close you want to be to the beach and how much space you need.</p>

          <h3>Colonia side (west of the highway):</h3>
          <ul>
            <li>1BR apartment: <strong>$400–$600 USD/month</strong></li>
            <li>2BR apartment in gated community: <strong>$540–$810 USD/month</strong></li>
            <li>Larger home: <strong>$1,000–$1,350 USD/month</strong></li>
          </ul>

          <h3>Beach side (east of the highway):</h3>
          <ul>
            <li>1BR condo near beach: <strong>$800–$1,200 USD/month</strong></li>
            <li>2BR condo with amenities: <strong>$1,200–$2,000 USD/month</strong></li>
            <li>Beachfront or luxury units: <strong>$2,000 USD and above</strong></li>
          </ul>

          <p>The colonia side gives you a genuinely local experience — quieter streets, lower prices, and a 10-minute bike ride to the beach. Most long-term expats and retirees who've been here a while end up on the colonia side. The beach side is for people who want Caribbean water as part of their daily view.</p>
          <p><strong>Pro tip:</strong> The best rental deals come through local Facebook groups like "Expats Puerto Morelos" — not through booking platforms. Long-term contracts (6+ months) can cut your rent by 20–30% versus short-term rates.</p>

          <h2>Food and Groceries</h2>
          <p><strong>Groceries: $250–$400 USD/month per person</strong></p>
          <p>Puerto Morelos has two supermarkets just off the highway that cover everything you need day-to-day. Local produce from the market is inexpensive. Imported and specialty items cost more, as they do anywhere in Mexico's tourist corridor.</p>
          <p><strong>Eating out: $150–$300 USD/month per person</strong></p>
          <p>The town has a wide range of options. Street tacos and local spots run $2–$5 per meal. Mid-range restaurants with seafood and international dishes land at $10–$20 per person. Fine dining exists but is the exception, not the rule.</p>
          <p>A couple eating out 3–4 times per week plus cooking at home typically spends <strong>$500–$700 USD</strong> on food combined.</p>

          <h2>Utilities</h2>
          <p>Puerto Morelos is a hot, humid Caribbean town. Air conditioning is not optional for most people — and that's where utility costs can surprise newcomers.</p>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Utility</th><th>Monthly Cost</th></tr></thead>
              <tbody>
                <tr><td>Electricity (with A/C)</td><td>$100–$200 USD</td></tr>
                <tr><td>Water</td><td>~$15 USD</td></tr>
                <tr><td>Gas (cooking/hot water)</td><td>$5–$10 USD</td></tr>
                <tr><td>Internet (50 Mbps+)</td><td>$25–$35 USD</td></tr>
                <tr><td>Mobile phone (Telcel)</td><td>$15–$20 USD</td></tr>
                <tr><td><strong>Total utilities</strong></td><td><strong>$160–$280 USD</strong></td></tr>
              </tbody>
            </table>
          </div>
          <p>CFE (Mexico's electric utility) bills every two months. If you run multiple air conditioners, your bill can be significant. Energy-efficient mini-split inverter units help considerably — most newer condos already have them.</p>

          <h2>Transportation</h2>
          <p>Puerto Morelos is a small town. You don't need a car to live here — many expats get around entirely by bicycle or on foot.</p>
          <ul>
            <li><strong>Bicycle:</strong> A used bike costs $60–$120 USD and covers most daily needs</li>
            <li><strong>Local taxis:</strong> $1.50–$3 USD for trips within town</li>
            <li><strong>Colectivo to Playa del Carmen:</strong> ~$2 USD</li>
            <li><strong>Colectivo to Cancún:</strong> ~$2–$3 USD</li>
            <li><strong>Car (if you have one):</strong> Gas, insurance, maintenance — estimate $150–$200 USD/month</li>
          </ul>
          <p>If you want regular access to Cancún or Playa del Carmen, a car makes life easier. But it's not essential.</p>

          <h2>Healthcare</h2>
          <p>This is one of the strongest arguments for Puerto Morelos over comparable destinations in the US or Canada.</p>
          <p>Puerto Morelos has local clinics for routine care. For anything more serious, Cancún is 20 minutes away and has world-class private hospitals including Hospiten and Galenia — both internationally accredited, both significantly cheaper than US equivalents.</p>
          <ul>
            <li><strong>Doctor visit (private clinic):</strong> $30–$60 USD</li>
            <li><strong>Dental cleaning:</strong> $30–$50 USD</li>
            <li><strong>Private health insurance:</strong> $100–$300 USD/month depending on age and coverage</li>
            <li><strong>Most medications:</strong> Available without prescription at a fraction of US prices</li>
          </ul>
          <p>Many expats — especially retirees — find that their healthcare costs drop dramatically after moving here even with full private insurance. If you're planning the actual move, our complete <Link to="/blog/moving-to-puerto-morelos-2026" className="text-[#2BBFBF] underline hover:text-[#0A2235] transition-colors">relocation guide to Puerto Morelos</Link> covers visas, rentals, and the full timeline.</p>

          <h2>Entertainment and Lifestyle</h2>
          <p>Puerto Morelos is not a nightlife destination. It's a reef, beach, yoga, and sunset town. That shapes what entertainment costs.</p>
          <ul>
            <li>Snorkeling or diving trip: $30–$60 USD</li>
            <li>Yoga class: $8–$15 USD</li>
            <li>Beer at a local bar: $2–$4 USD</li>
            <li>Cinema (Cancún): $5–$8 USD</li>
            <li>Day trip to cenotes: $20–$40 USD</li>
          </ul>
          <p>Most people who live here find their entertainment spending drops naturally — the beach is free, the reef is accessible, and the pace of life doesn't demand constant spending.</p>

          <h2>Sample Monthly Budgets</h2>

          <h3>Budget lifestyle (single person, colonia side):</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Category</th><th>Cost</th></tr></thead>
              <tbody>
                <tr><td>Rent</td><td>$500</td></tr>
                <tr><td>Food</td><td>$400</td></tr>
                <tr><td>Utilities</td><td>$180</td></tr>
                <tr><td>Transport</td><td>$30</td></tr>
                <tr><td>Healthcare</td><td>$100</td></tr>
                <tr><td>Extras</td><td>$150</td></tr>
                <tr><td><strong>Total</strong></td><td><strong>~$1,360 USD/month</strong></td></tr>
              </tbody>
            </table>
          </div>

          <h3>Comfortable lifestyle (single person, near beach):</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Category</th><th>Cost</th></tr></thead>
              <tbody>
                <tr><td>Rent</td><td>$900</td></tr>
                <tr><td>Food</td><td>$600</td></tr>
                <tr><td>Utilities</td><td>$220</td></tr>
                <tr><td>Transport</td><td>$80</td></tr>
                <tr><td>Healthcare</td><td>$150</td></tr>
                <tr><td>Extras</td><td>$300</td></tr>
                <tr><td><strong>Total</strong></td><td><strong>~$2,250 USD/month</strong></td></tr>
              </tbody>
            </table>
          </div>

          <h3>Couple, comfortable lifestyle:</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Category</th><th>Cost</th></tr></thead>
              <tbody>
                <tr><td>Rent</td><td>$1,200</td></tr>
                <tr><td>Food</td><td>$900</td></tr>
                <tr><td>Utilities</td><td>$250</td></tr>
                <tr><td>Transport</td><td>$100</td></tr>
                <tr><td>Healthcare</td><td>$250</td></tr>
                <tr><td>Extras</td><td>$400</td></tr>
                <tr><td><strong>Total</strong></td><td><strong>~$3,100 USD/month</strong></td></tr>
              </tbody>
            </table>
          </div>

          <h2>Puerto Morelos vs. Playa del Carmen vs. Tulum</h2>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Category</th><th>Puerto Morelos</th><th>Playa del Carmen</th><th>Tulum</th></tr></thead>
              <tbody>
                <tr><td>1BR near beach</td><td className="text-[#2BBFBF] font-medium">$800–$1,200</td><td>$1,000–$1,600</td><td>$1,200–$2,000</td></tr>
                <tr><td>Groceries/month</td><td className="text-[#2BBFBF] font-medium">$250–$400</td><td>$300–$450</td><td>$350–$500</td></tr>
                <tr><td>Eating out (meal)</td><td className="text-[#2BBFBF] font-medium">$5–$20</td><td>$8–$25</td><td>$10–$35</td></tr>
                <tr><td>Overall vibe</td><td className="text-[#2BBFBF] font-medium">Quiet, local</td><td>Urban, busy</td><td>Trendy, crowded</td></tr>
              </tbody>
            </table>
          </div>
          <p>Puerto Morelos consistently comes in <strong>15–25% cheaper</strong> than Playa del Carmen and <strong>20–35% cheaper</strong> than Tulum for comparable quality of living.</p>

          <h2>Frequently Asked Questions</h2>

          <p><strong>Is Puerto Morelos expensive for Mexico?</strong></p>
          <p>Compared to inland Mexican cities, yes — it's a tourist destination on the Caribbean coast. But compared to Playa del Carmen, Tulum, or any comparable beach town in the US or Europe, it's very reasonable.</p>

          <p><strong>Can you live in Puerto Morelos on $2,000 USD per month?</strong></p>
          <p>Yes, comfortably. A single person with modest rent on the colonia side, cooking most meals at home, and living a beach-focused lifestyle can live well on $1,500–$2,000 USD/month.</p>

          <p><strong>Is Puerto Morelos good for retirees?</strong></p>
          <p>Very. The combination of low cost of living, proximity to Cancún's hospitals, a close-knit expat community, warm weather year-round, and a relaxed pace makes it one of the better retirement options in the Riviera Maya.</p>

          <p><strong>Do I need a car in Puerto Morelos?</strong></p>
          <p>Not necessarily. The town is small and bikeable. Colectivos to Playa del Carmen and Cancún are cheap and frequent. A car adds comfort but is not essential for daily life.</p>

          <p><strong>How does Puerto Morelos compare to Playa del Carmen for cost of living?</strong></p>
          <p>Puerto Morelos is consistently 15–25% cheaper across rent, food, and entertainment. You trade urban amenities and nightlife for a quieter, more authentic lifestyle.</p>

          <p><strong>What is the weather like in Puerto Morelos?</strong></p>
          <p>Tropical year-round. Dry season runs November to April — peak season for snowbirds and tourists. Wet season May to October brings afternoon rains and occasional hurricanes, but also lower prices and fewer crowds.</p>

          <h2>The Bottom Line</h2>
          <p>Puerto Morelos offers one of the best value propositions on the Riviera Maya for people who want Caribbean living without Tulum prices or Playa del Carmen crowds.</p>
          <p>It won't be the cheapest place you've ever lived. But it might be the best value for what you get: warm water, a healthy reef, a genuine community, and 20-minute access to an international airport — for a fraction of what similar quality costs in the US, Canada, or Europe.</p>
          </>)}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#0A2235] rounded-[20px] p-8 md:p-12 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-4">
            {es ? '¿Considerando Puerto Morelos?' : 'Considering Puerto Morelos?'}
          </h2>
          <p className="font-inter text-[#FAFAF7]/70 mb-8 max-w-xl mx-auto leading-relaxed">
            {es ? 'Si estás considerando mudarte o invertir en propiedad aquí, Marea Riviera se especializa exactamente en este mercado.' : 'If you\'re considering making the move or investing in property here, Marea Riviera specializes in exactly this market. We handle due diligence, developer vetting, and full support throughout the process.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full px-8 py-6 text-base" data-testid="blog-cta-whatsapp">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">{es ? 'Agendar consulta gratuita' : 'Schedule a free consultation'}</a>
            </Button>
            <Button onClick={scrollToContact} variant="outline" className="border-[#2BBFBF] text-[#2BBFBF] hover:bg-[#2BBFBF] hover:text-[#0A2235] rounded-full px-8 py-6 text-base" data-testid="blog-cta-contact">
              {es ? 'Ir al formulario de contacto' : 'Go to contact form'}
            </Button>
          </div>
          <p className="font-inter text-[#FAFAF7]/40 text-sm mt-6">{es ? 'Sin compromiso. Respuesta en menos de 2 horas.' : 'No commitment. Response within 2 hours.'}</p>
        </div>

        {/* Internal links */}
        <div className="mt-12 pt-8 border-t border-[#0A2235]/10 space-y-3">
          <p className="font-inter text-[#0A2235]/70 text-sm font-medium">{es ? 'Artículos relacionados:' : 'Related articles:'}</p>
          <ul className="space-y-2">
            <li><Link to="/blog/living-and-investing-in-puerto-morelos-complete-guide-2026" className="font-inter text-[#2BBFBF] text-sm underline hover:text-[#0A2235] transition-colors">Living & Investing in Puerto Morelos: Complete Guide</Link></li>
            <li><Link to="/blog/moving-to-puerto-morelos-2026" className="font-inter text-[#2BBFBF] text-sm underline hover:text-[#0A2235] transition-colors">{es ? 'Mudarse a Puerto Morelos: Guía de Reubicación' : 'Moving to Puerto Morelos: Relocation Guide'}</Link></li>
            <li><Link to="/blog/investing-tulum-real-estate-foreign-buyers" className="font-inter text-[#2BBFBF] text-sm underline hover:text-[#0A2235] transition-colors">Investing in Tulum Real Estate</Link></li>
          </ul>
        </div>

        {/* Author */}
        <div className="mt-8 pt-8 border-t border-[#0A2235]/10">
          <p className="font-inter text-[#0A2235]/50 text-sm italic leading-relaxed">
            {es ? 'Marea Riviera es un brokerage de inversión inmobiliaria especializado en Tulum, Playa del Carmen y Puerto Morelos. Trabajamos exclusivamente con desarrollos que tienen MIA vigente y estructura legal sólida.' : 'Marea Riviera is a real estate investment brokerage specializing in Tulum, Playa del Carmen, and Puerto Morelos. We work exclusively with developments that have active MIA and solid legal structure.'}
          </p>
        </div>
      </article>
    </div>
  );
}
